import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Flame, Clock, Target, TrendingUp, Play, BookOpen, Lock, Crown, Trophy } from "lucide-react";
import api, { formatSeconds } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import TodaysTargets from "../components/TodaysTargets";
import WelcomeTrialPopup from "../components/WelcomeTrialPopup";
import TelegramRecommendCard from "../components/TelegramRecommendCard";

// Lightweight goal-based dashboard greeting. Returns the standard nudge
// when the user hasn't picked a goal yet.
function goalSubGreeting(track) {
  switch (track) {
    case "NEET": return "Welcome back, future doctor 🩺";
    case "JEE":  return "Keep building consistency, future engineer ⚡";
    case "UPSC": return "One day at a time, future officer 🇮🇳";
    case "SSC":  return "Stay sharp, future achiever 🎯";
    default:     return "Let's make today count.";
  }
}
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

const AXIS_TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 12 };
const TOOLTIP_STYLE = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 };
const BAR_RADIUS = [6, 6, 0, 0];

const StatCard = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      <div className={`grid h-9 w-9 place-items-center rounded-lg ${accent || "bg-emerald-500/10 text-emerald-600"}`}>
        <Icon size={16} />
      </div>
    </div>
    <div className="mt-3 font-heading text-3xl font-bold">{value}</div>
    {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { effectivePremium, access } = useAccess();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  const days = data?.weekly?.map((d) => ({
    day: new Date(d.date).toLocaleDateString("en", { weekday: "short" }),
    minutes: Math.round(d.seconds / 60),
  })) || [];

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <WelcomeTrialPopup />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Today</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="dashboard-greeting">
            Hey {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400" data-testid="dashboard-subgreeting">
            {goalSubGreeting(user?.exam_track)}
          </p>
        </div>
        <Button onClick={() => navigate("/timer")} data-testid="dashboard-start-session" className="bg-emerald-500 hover:bg-emerald-600 text-white">
          <Play size={16} className="mr-1.5" /> Start study session
        </Button>
      </div>

      <div className={`grid grid-cols-2 gap-4 ${effectivePremium && access?.achievements_summary ? "sm:grid-cols-3 md:grid-cols-5" : "md:grid-cols-4"}`}>
        <StatCard icon={Clock} label="Today" value={formatSeconds(data?.today_seconds || 0)} sub="Study time" testid="stat-today" />
        <StatCard icon={Flame} label="Streak" value={`${data?.streak || 0}d`} sub="Consecutive days" testid="stat-streak" accent="bg-amber-500/10 text-amber-600" />
        <StatCard icon={Target} label="Syllabus" value={`${Math.round(data?.syllabus_pct || 0)}%`} sub={`${data?.completed_chapters || 0} / ${data?.total_chapters || 0} chapters`} testid="stat-syllabus" accent="bg-blue-500/10 text-blue-600" />
        <StatCard icon={TrendingUp} label="This week" value={formatSeconds(data?.week_seconds || 0)} sub="Total study time" testid="stat-week" accent="bg-purple-500/10 text-purple-600" />
        {/* Premium-only badges card — reuses already-fetched /me/access data.
            Zero new API calls. Hidden for free users (same gating as the
            sidebar chip that this replaces). Tapping it opens Profile where
            the full grid lives. */}
        {effectivePremium && access?.achievements_summary && (
          <Link
            to="/profile"
            data-testid="stat-badges"
            className="rounded-2xl border border-zinc-200 bg-white p-5 transition-colors hover:border-amber-400/50 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-amber-400/40"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Badges</span>
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-amber-500/15 text-amber-600">
                <Trophy size={16}/>
              </div>
            </div>
            <div className="mt-3 font-heading text-3xl font-bold tabular-nums">
              {access.achievements_summary.earned_count}
              <span className="text-base font-medium text-zinc-500"> / {access.achievements_summary.total_count}</span>
            </div>
            <div className="mt-1 text-xs text-zinc-500">Earned</div>
          </Link>
        )}
      </div>

      {effectivePremium ? (
        <TodaysTargets />
      ) : (
        <div
          className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900"
          data-testid="targets-locked-card"
        >
          <div className="flex items-start gap-4">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600">
              <Lock size={18}/>
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-1.5">
                Today&apos;s Targets <Crown size={14} className="text-amber-500"/>
              </h3>
              <p className="mt-1 text-sm text-zinc-500">
                Plan your daily focus, tick off small wins, and build the streak. Unlock with Syllox Premium.
              </p>
              <p className="mt-1.5 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="targets-locked-pricing">₹49/month · ₹299/year</p>
              <Button
                onClick={() => navigate("/premium")}
                className="mt-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700"
                data-testid="targets-unlock-btn"
              >
                <Crown size={14} className="mr-1.5"/> Unlock Today&apos;s Targets
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram recommendation — persistent per-user dismissal, no re-show
          once the user joins or closes. */}
      <TelegramRecommendCard />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900 lg:col-span-2" data-testid="weekly-chart-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold">Weekly progress</h3>
              <p className="text-xs text-zinc-500">Minutes studied per day</p>
            </div>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={AXIS_TICK} axisLine={false} tickLine={false}/>
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="minutes" fill="#10B981" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="syllabus-progress-card">
          <h3 className="font-heading text-lg font-semibold">Syllabus completion</h3>
          <p className="text-xs text-zinc-500">Overall progress across all subjects</p>
          <div className="mt-6 flex flex-col items-center">
            <div className="relative h-32 w-32">
              <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                <circle cx="50" cy="50" r="42" stroke="hsl(var(--border))" strokeWidth="10" fill="none" />
                <circle
                  cx="50" cy="50" r="42" stroke="#10B981" strokeWidth="10" fill="none"
                  strokeDasharray={`${(data?.syllabus_pct || 0) * 2.64} 264`} strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 grid place-items-center font-heading text-2xl font-bold">{Math.round(data?.syllabus_pct || 0)}%</div>
            </div>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/syllabus")} data-testid="open-syllabus-btn">
              <BookOpen size={14} className="mr-1.5"/> Open syllabus
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="recent-completed-card">
        <h3 className="font-heading text-lg font-semibold">Recently completed</h3>
        <p className="text-xs text-zinc-500">Your last finished chapters</p>
        <div className="mt-4 divide-y divide-zinc-100 dark:divide-zinc-800">
          {(data?.recent_completed?.length || 0) === 0 ? (
            <div className="py-6 text-center text-sm text-zinc-500">No completed chapters yet. Start ticking off your syllabus.</div>
          ) : (
            data.recent_completed.map((c) => (
              <div key={c.chapter_id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-zinc-500">{c.subject_name}</div>
                </div>
                <div className="text-xs text-zinc-500">{c.completed_at && new Date(c.completed_at).toLocaleDateString()}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
